import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Calendar, Clock, MapPin, Users, TrendingUp, Download, MessageCircle, ExternalLink, Trophy, Star, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "@/hooks/useAuth";
import { eventService } from "@/services/eventService";
import { Event } from "@/types/event";
import NavigationBar from "@/components/NavigationBar";
import { useToast } from "@/hooks/use-toast";
import { AdvancedAnalytics } from "@/components/AdvancedAnalytics";
import { EventComments } from "@/components/EventComments";
import { CalendarIntegration } from "@/components/CalendarIntegration";
import { ExportManager } from "@/components/ExportManager";

const Analytics = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const eventId = searchParams.get('event');

  useEffect(() => {
    const loadEvent = async () => {
      if (!eventId) {
        navigate('/dashboard');
        return;
      }

      try {
        const eventData = await eventService.getEvent(eventId);
        if (!eventData) {
          toast({
            title: "Event not found",
            description: "The requested event could not be found.",
            variant: "destructive",
          });
          navigate('/dashboard');
          return;
        }

        // Check if user owns this event
        if (eventData.userId !== user?.id) {
          toast({
            title: "Access denied",
            description: "You don't have permission to view analytics for this event.",
            variant: "destructive",
          });
          navigate('/dashboard');
          return;
        }

        setEvent(eventData);
      } catch (error) {
        console.error('Error loading event:', error);
        toast({
          title: "Error",
          description: "Failed to load event data.",
          variant: "destructive",
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadEvent();
    }
  }, [eventId, user, navigate, toast]);

  const getIconForOption = (type: string) => {
    switch (type) {
      case 'date': return <Calendar className="w-4 h-4" />;
      case 'time': return <Clock className="w-4 h-4" />;
      case 'location': return <MapPin className="w-4 h-4" />;
      default: return null;
    }
  };

  const formatOptionValue = (option: any) => {
    switch (option.type) {
      case 'date':
        return new Date(option.value).toLocaleDateString();
      case 'time':
        return new Date(`2000-01-01T${option.value}`).toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case 'location':
        return option.value;
      default:
        return option.value;
    }
  };

  const exportResults = () => {
    if (!event) return;
    
    const results = event.options.map(option => ({
      type: option.type,
      value: formatOptionValue(option),
      votes: option.votes,
      percentage: event.totalVoters > 0 ? Math.round((option.votes / event.totalVoters) * 100) : 0
    }));

    const csvContent = [
      ['Option Type', 'Option Value', 'Votes', 'Percentage'],
      ...results.map(r => [r.type, r.value, r.votes, `${r.percentage}%`])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.name}_results.csv`;
    a.click();
    
    toast({
      title: "Export complete",
      description: "Results have been downloaded as CSV file.",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-background">
        <NavigationBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-background">
        <NavigationBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Event not found</div>
        </div>
      </div>
    );
  }

  const votingData = event.options.map(option => ({
    name: formatOptionValue(option),
    votes: option.votes,
    type: option.type
  }));

  const participationRate = event.guests.length > 0 ? 
    Math.round((event.totalVoters / event.guests.length) * 100) : 0;

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];

  // Group options by type and find winners
  const getTopOptionsByType = () => {
    const dateOptions = event.options.filter(o => o.type === 'date').sort((a, b) => b.votes - a.votes);
    const timeOptions = event.options.filter(o => o.type === 'time').sort((a, b) => b.votes - a.votes);
    const locationOptions = event.options.filter(o => o.type === 'location').sort((a, b) => b.votes - a.votes);

    return {
      date: dateOptions[0],
      time: timeOptions[0],
      location: locationOptions[0],
      all: {
        date: dateOptions,
        time: timeOptions,
        location: locationOptions
      }
    };
  };

  const topOptions = getTopOptionsByType();

  return (
    <div className="min-h-screen bg-gradient-background">
      <NavigationBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold">{event.name} Analytics</h1>
              <p className="text-muted-foreground">Comprehensive event insights and management</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
            <TabsTrigger value="discussion">Discussion</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="export">Export</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Top Winners Section */}
            {(topOptions.date || topOptions.time || topOptions.location) && (
              <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Trophy className="w-6 h-6 text-primary" />
                    Top Choices
                  </CardTitle>
                  <CardDescription>Most popular options by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    {topOptions.date && (
                      <div className="p-4 rounded-lg bg-card border-2 border-primary/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-primary/10 rounded-bl-full" />
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Winning Date</span>
                          </div>
                          <div className="text-2xl font-bold mb-2">{formatOptionValue(topOptions.date)}</div>
                          <div className="flex items-center gap-2">
                            <Badge variant="default" className="gap-1">
                              <Star className="w-3 h-3" />
                              {topOptions.date.votes} votes
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {event.totalVoters > 0 ? Math.round((topOptions.date.votes / event.totalVoters) * 100) : 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {topOptions.time && (
                      <div className="p-4 rounded-lg bg-card border-2 border-primary/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-primary/10 rounded-bl-full" />
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Winning Time</span>
                          </div>
                          <div className="text-2xl font-bold mb-2">{formatOptionValue(topOptions.time)}</div>
                          <div className="flex items-center gap-2">
                            <Badge variant="default" className="gap-1">
                              <Star className="w-3 h-3" />
                              {topOptions.time.votes} votes
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {event.totalVoters > 0 ? Math.round((topOptions.time.votes / event.totalVoters) * 100) : 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {topOptions.location && (
                      <div className="p-4 rounded-lg bg-card border-2 border-primary/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-primary/10 rounded-bl-full" />
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Winning Location</span>
                          </div>
                          <div className="text-2xl font-bold mb-2">{formatOptionValue(topOptions.location)}</div>
                          <div className="flex items-center gap-2">
                            <Badge variant="default" className="gap-1">
                              <Star className="w-3 h-3" />
                              {topOptions.location.votes} votes
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {event.totalVoters > 0 ? Math.round((topOptions.location.votes / event.totalVoters) * 100) : 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-6 md:grid-cols-3">
              {/* Key Metrics */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Participation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{participationRate}%</div>
                  <p className="text-sm text-muted-foreground">
                    {event.totalVoters} of {event.guests.length} guests voted
                  </p>
                  <Progress value={participationRate} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Total Options
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{event.options.length}</div>
                  <p className="text-sm text-muted-foreground">
                    Voting options available
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {event.isActive ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Closed</Badge>
                    )}
                    {event.deadline && (
                      <p className="text-sm text-muted-foreground">
                        Deadline: {new Date(event.deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Voting Results Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Voting Results</CardTitle>
                  <CardDescription>Vote distribution across all options</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={votingData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="votes" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Option Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Option Types</CardTitle>
                  <CardDescription>Distribution of option types</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Dates', value: event.options.filter(o => o.type === 'date').length },
                          { name: 'Times', value: event.options.filter(o => o.type === 'time').length },
                          { name: 'Locations', value: event.options.filter(o => o.type === 'location').length }
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label
                      >
                        {COLORS.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Results by Category */}
            <div className="grid gap-6 lg:grid-cols-3">
              {['date', 'time', 'location'].map((type) => {
                const options = topOptions.all[type as keyof typeof topOptions.all];
                if (options.length === 0) return null;

                return (
                  <Card key={type}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 capitalize">
                        {getIconForOption(type)}
                        {type} Options
                      </CardTitle>
                      <CardDescription>Ranked by votes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {options.map((option, index) => {
                          const percentage = event.totalVoters > 0 ? 
                            Math.round((option.votes / event.totalVoters) * 100) : 0;
                          const isWinner = index === 0;
                          
                          return (
                            <div 
                              key={option.id} 
                              className={`p-3 rounded-lg border-2 transition-all ${
                                isWinner 
                                  ? 'border-primary bg-primary/5 shadow-md' 
                                  : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2 flex-1 min-w-0">
                                  <div className={`min-w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    isWinner 
                                      ? 'bg-primary text-primary-foreground' 
                                      : 'bg-muted text-muted-foreground'
                                  }`}>
                                    {isWinner ? <Trophy className="w-3 h-3" /> : index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className={`font-medium truncate ${isWinner ? 'text-primary' : ''}`}>
                                      {formatOptionValue(option)}
                                    </div>
                                    <Progress value={percentage} className="mt-1.5 h-1.5" />
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-bold text-sm">{option.votes}</div>
                                  <div className="text-xs text-muted-foreground">{percentage}%</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="advanced">
            <AdvancedAnalytics event={event} />
          </TabsContent>

          <TabsContent value="discussion">
            <EventComments eventId={event.id} isEventOwner={true} />
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarIntegration event={event} />
          </TabsContent>

          <TabsContent value="export">
            <ExportManager event={event} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Analytics;
